import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Play } from "lucide-react";

export default function VendorProfile() {
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/vendors/${id}`).then((r) => setVendor(r.data));
    api.get("/products", { params: { vendor_id: id } }).then((r) => setProducts(r.data));
  }, [id]);

  if (!vendor) return <div className="max-w-6xl mx-auto px-6 py-16 text-[#718096]">Loading…</div>;

  const gallery = vendor.gallery || [];

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <div className="card-flat overflow-hidden">
              {vendor.avatar_url ? (
                <img src={vendor.avatar_url} alt={vendor.business_name} className="w-full aspect-square object-cover" />
              ) : <div className="w-full aspect-square bg-[#F2EFEB] grid place-items-center font-serif text-7xl text-[#C3B9A6]">स</div>}
              <div className="p-5">
                <div className="badge-swadeshi">Verified maker</div>
                <div className="mt-3 label-caps">Based in</div>
                <div className="font-semibold text-[#13294B]">{vendor.location}</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="label-caps mb-2">Maker profile</div>
            <h1 className="text-4xl md:text-5xl">{vendor.business_name}</h1>
            <p className="mt-4 text-[#4A5568] leading-relaxed">{vendor.description}</p>
            {vendor.story && (
              <div className="mt-8 p-6 border-l-4 border-[#E25A24] bg-[#FFF3EA] rounded-r-2xl">
                <div className="label-caps mb-2">The story</div>
                <p className="text-[#0D141C] leading-relaxed">{vendor.story}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-8">
          <div className="mb-6">
            <div className="label-caps mb-2">Inside the workshop</div>
            <h2 className="text-3xl">Behind the craft</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vendor-gallery">
            {gallery.map((g, i) => (
              <button
                key={g.id || i}
                onClick={() => setLightbox(g)}
                data-testid={`gallery-item-${i}`}
                className={`relative overflow-hidden rounded-xl bg-[#F2EFEB] hover:shadow-[0_8px_32px_rgba(19,41,75,0.08)] transition-shadow ${i % 5 === 0 ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-square"}`}
              >
                {g.type === "video" ? (
                  <>
                    <video src={g.url} className="w-full h-full object-cover" muted playsInline />
                    <div className="absolute inset-0 grid place-items-center bg-black/25">
                      <Play className="w-8 h-8 text-white fill-white" />
                    </div>
                  </>
                ) : (
                  <img src={g.url} alt={g.caption || "Workshop"} className="w-full h-full object-cover" loading="lazy" />
                )}
                {g.caption && <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white text-xs">{g.caption}</div>}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-3xl mb-6">Their craft</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-6" data-testid="gallery-lightbox">
          <button className="absolute top-6 right-6 text-white text-2xl" onClick={() => setLightbox(null)}>×</button>
          {lightbox.type === "video" ? (
            <video src={lightbox.url} controls autoPlay className="max-h-[85vh] max-w-full rounded-lg" />
          ) : (
            <img src={lightbox.url} alt="" className="max-h-[85vh] max-w-full rounded-lg" />
          )}
          {lightbox.caption && <div className="absolute bottom-8 left-0 right-0 text-center text-white text-sm">{lightbox.caption}</div>}
        </div>
      )}
    </div>
  );
}
